import type { Session } from '@supabase/supabase-js';
import { config } from '@/api/config';
import { supabase } from '@/api/supabase';
import type { ApiError } from '@/types';

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
}

class ApiClient {
  private baseUrl: string;
  private cachedSession: Session | null = null;
  private sessionInitialized = false;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    this.initSession();
  }

  private async initSession(): Promise<void> {
    // Get initial session once
    const {
      data: { session },
    } = await supabase.auth.getSession();
    this.cachedSession = session;
    this.sessionInitialized = true;

    // Subscribe to auth changes to keep session updated
    supabase.auth.onAuthStateChange((_event, session) => {
      this.cachedSession = session;
    });
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    // Wait for initial session if not yet loaded
    if (!this.sessionInitialized) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      this.cachedSession = session;
      this.sessionInitialized = true;
    }

    const profileId = localStorage.getItem('banquinha_profile_id');

    return {
      'Content-Type': 'application/json',
      ...(this.cachedSession?.access_token && {
        Authorization: `Bearer ${this.cachedSession.access_token}`,
      }),
      ...(profileId && {
        'X-Profile-Id': profileId,
      }),
    };
  }

  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, ...fetchOptions } = options;
    const url = this.buildUrl(endpoint, params);
    const headers = await this.getAuthHeaders();

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        ...headers,
        ...fetchOptions.headers,
      },
    });

    if (!response.ok) {
      const error: ApiError = await response.json().catch(() => ({
        code: 'UNKNOWN_ERROR',
        message: response.statusText,
      }));
      throw error;
    }

    // Handle empty responses (204 No Content or empty body)
    const text = await response.text();
    if (!text) {
      // Return null for empty responses - callers should handle this
      return null as T;
    }

    return JSON.parse(text) as T;
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>,
  ): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET', params });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(config.api.baseUrl);
